import { Fraction } from '@learnforge/demo-math/numeric';
import type { SequenceSpec } from '../algebra/sequence';

export interface SequencePreset {
  id: string;
  kind: 'arithmetic' | 'geometric' | 'recurrence';
  a1: Fraction;
  d?: Fraction; // For arithmetic and recurrence
  r?: Fraction; // For geometric
  c?: Fraction; // For recurrence (coefficient in a_{n+1} = c*a_n + d)
  advancedOnly?: boolean;
  toSpec(): SequenceSpec;
}

const F = (n: number, d: number = 1): Fraction => new Fraction(n, d);

export const SEQUENCE_PRESETS: SequencePreset[] = [
  {
    id: 'arithmetic-foundation-1',
    kind: 'arithmetic',
    a1: F(2),
    d: F(3),
    toSpec() {
      return { kind: 'arithmetic' as const, a1: this.a1, d: this.d! };
    },
  },
  {
    id: 'geometric-foundation-1',
    kind: 'geometric',
    a1: F(1),
    r: new Fraction(1, 2),
    toSpec() {
      return { kind: 'geometric' as const, a1: this.a1, r: this.r! };
    },
  },
  {
    id: 'recurrence-advanced-1',
    kind: 'recurrence',
    a1: F(3),
    c: F(2),
    d: F(1),
    advancedOnly: true,
    toSpec() {
      return { kind: 'recurrence' as const, a1: this.a1, c: this.c!, d: this.d! };
    },
  },
  {
    id: 'geometric-divergent',
    kind: 'geometric',
    a1: F(1),
    r: F(2),
    advancedOnly: true,
    toSpec() {
      return { kind: 'geometric' as const, a1: this.a1, r: this.r! };
    },
  },
];

/**
 * Validate a sequence preset.
 * Ensures all required fields are present and correctly typed.
 */
export function isValidPreset(preset: unknown): preset is SequencePreset {
  if (!preset || typeof preset !== 'object') {
    return false;
  }

  const p = preset as Record<string, unknown>;

  // Check required fields
  if (typeof p.id !== 'string' || !p.id) {
    return false;
  }

  if (!['arithmetic', 'geometric', 'recurrence'].includes(p.kind as string)) {
    return false;
  }

  if (!(p.a1 instanceof Fraction)) {
    return false;
  }

  // Check kind-specific fields
  if (p.kind === 'arithmetic') {
    if (!(p.d instanceof Fraction)) {
      return false;
    }
  } else if (p.kind === 'geometric') {
    if (!(p.r instanceof Fraction)) {
      return false;
    }
  } else if (p.kind === 'recurrence') {
    if (!(p.c instanceof Fraction) || !(p.d instanceof Fraction)) {
      return false;
    }
  }

  // Check optional fields
  if (p.advancedOnly !== undefined && typeof p.advancedOnly !== 'boolean') {
    return false;
  }

  // Check toSpec is a function
  return typeof p.toSpec === 'function';
}
